import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  AuditSchema,
  type Audit,
  type AuditId,
} from '#packages/data-collection/models/audit';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import { type Clock } from '#packages/data-collection/services/clock';

import { useLoadAuditByIdQuery } from '#apps/client-web/features/audit/hooks/use-load-audit-by-id-query';
import { type AuditService } from '#apps/client-web/features/audit/services/audit';
import { useUpdateAuditMutation } from '#apps/client-web/features/audit/hooks/use-update-audit-mutation';
import { AUDITS_QUERY_KEY } from '#apps/client-web/features/audit/types/react-query';
import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';

import { type EditAuditFormData } from './types';

export type AuditSettingsPageViewModel = ReturnType<
  typeof useAuditSettingsPageViewModel
>;

type AuditSettingsPageViewModelProps = LoggingContext & {
  auditService: AuditService;
  queryClient: QueryClient;

  clock: Clock;
};

export const useAuditSettingsPageViewModel = ({
  auditService,
  queryClient,

  logger,
}: AuditSettingsPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });
  const { auditId } = useParams<{ auditId: AuditId }>();

  const auditDetailsQuery = useLoadAuditByIdQuery({
    auditService,
    queryClient,
    collectionName: 'active',
    auditId: auditId!,
  });

  const updateAuditMutation = useUpdateAuditMutation({
    auditService,
    queryClient,
  });

  const form = useForm<EditAuditFormData>({
    reValidateMode: 'onChange',

    defaultValues: {
      types: [],
    },
  });

  const onUpdate = useCallback(
    (formData: EditAuditFormData) => {
      logger?.debug({ auditId, formData }, 'Updating audit');

      const updatedAudit = AuditSchema.decode({
        ...AuditSchema.encode(auditDetailsQuery.data!),

        title: formData.title,
        types: formData.types,
        project: formData.project || undefined,
        auditDate: formData.auditDate.toISOString(),
      });

      updateAuditMutation.mutate(
        { collectionName: 'active', updatedAudit },
        {
          onSuccess: (createdAudit: Audit) => {
            logger?.debug(
              { auditId: createdAudit.id },
              'Audit updated successfully',
            );
            // invalidate audit list queries
            queryClient
              .invalidateQueries({
                queryKey: [AUDITS_QUERY_KEY],
              })
              .then(() => {
                logger?.debug('Audit list queries invalidated');
                goBack();
              });
          },
          onError: (err) => {
            logger?.error({ err }, 'Failed to update audit');
            throw err;
          },
        },
      );
    },
    [updateAuditMutation.mutate],
  );

  // sync form default values when audit details are loaded
  useEffect(() => {
    if (!auditDetailsQuery.isSuccess) {
      return;
    }

    logger?.debug(
      { auditDetails: auditDetailsQuery.data },
      'Setting form default values',
    );

    // update form default values
    form.reset({
      title: auditDetailsQuery.data.title,
      types: auditDetailsQuery.data.types,
      project: auditDetailsQuery.data.project || '',
      auditDate: auditDetailsQuery.data.auditDate.toDate(),
    });
  }, [auditDetailsQuery.data, auditDetailsQuery.isSuccess, form]);

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (updateAuditMutation.isIdle || updateAuditMutation.isSuccess) {
      return { state: 'idle' };
    }

    if (updateAuditMutation.isPending) {
      return { state: 'pending' };
    }

    if (updateAuditMutation.isError) {
      return { state: 'error', error: updateAuditMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    updateAuditMutation.isIdle,
    updateAuditMutation.isPending,
    updateAuditMutation.isSuccess,
    updateAuditMutation.isError,
    updateAuditMutation.error,
  ]);

  const loadAuditDetailsState = useMemo<QueryPageState<Audit>>(() => {
    if (auditDetailsQuery.isSuccess) {
      return { state: 'success', data: auditDetailsQuery.data };
    }

    if (auditDetailsQuery.isLoading || auditDetailsQuery.isPending) {
      return { state: 'loading' };
    }

    if (auditDetailsQuery.isError) {
      return { state: 'error', error: auditDetailsQuery.error };
    }

    throw new Error('Unhandled query state');
  }, [
    auditDetailsQuery.isSuccess,
    auditDetailsQuery.isLoading,
    auditDetailsQuery.isPending,
    auditDetailsQuery.isError,
    auditDetailsQuery.error,
  ]);

  return {
    auditDetailsQuery,
    loadAuditDetailsState,

    form,
    onSubmit: form.handleSubmit(onUpdate),
    updateMutationState,

    auditId,
  };
};
